import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  User,
  Mail,
  Building2,
  Warehouse,
  ArrowLeft,
  ArrowRight,
  Check,
} from "lucide-react";

import Layout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import PageHeader from "src/components/common/PageHeader";
import Form, { FormField } from "src/components/ui/Form";
import Input from "src/components/ui/Input";
import Button from "src/components/ui/Button";
import Dropdown from "src/components/ui/Dropdown";
import IdentifierField from "src/components/auth/IdentifierField";
import PasswordField from "src/components/auth/PasswordField";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as tenantActions from "src/redux/slices/tenantSlice";
import { toast } from "react-toastify";
import authTenantApis from "src/api/authTenant";
import useIdentifierAvailability from "src/hooks/useIdentifierAvailability";
import {
  validateRequiredName,
  validateEmail,
  validateMobile,
  validatePassword,
} from "src/utils/authFormValidation";
import {
  LOCATION_LABEL_PRESETS,
  DEFAULT_LOCATION_LABEL_PRESET,
} from "src/constants/locationLabelPresets";

// Tenant creation now happens in a modal from the list page (see
// TenantListPage.tsx) — this route/component only ever handles edit mode.
const TenantForm: React.FC = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { editTenant, tenantDetails } = useAppSelector((state) => state.tenant);

  useEffect(() => {
    if (tenantId) {
      dispatch(tenantActions.getTenantDetailsRequest(tenantId));
    }
  }, [dispatch, tenantId]);

  useEffect(() => {
    dispatch(tenantActions.resetEditTenant());
    return () => {
      dispatch(tenantActions.resetEditTenant());
    };
  }, [dispatch]);

  useEffect(() => {
    if (tenantDetails.error) {
      toast.error("Failed to fetch tenant details");
    }
  }, [tenantDetails.error]);

  useEffect(() => {
    if (editTenant.data) {
      toast.success("Tenant modified successfully");
      handleClose();
    } else if (editTenant.error) {
      toast.error(`Error: ${editTenant.error}`);
    }
  }, [editTenant]);

  const handleClose = () => {
    dispatch(tenantActions.resetEditTenant());
    navigate("/admin/tenant/list");
  };

  return (
    <Layout
      type="admin"
      meta={{
        title: "Edit Tenant | Invyntra",
        description: "Edit tenant details.",
        keywords: "tenants, details, invyntra",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title="Edit Tenant"
          showBack
          breadcrumbData={[
            { label: "Home", path: "/admin/dashboard" },
            { label: "Tenants", path: "/admin/tenants/list" },
            { label: "Edit Tenant", path: "" },
          ]}
        />

        <TenantEditForm
          tenantDetails={tenantDetails}
          loading={editTenant.loading || tenantDetails.loading}
          onSubmit={(data) =>
            dispatch(
              tenantActions.editTenantRequest({
                ...(data as any),
                _id: tenantDetails.data?._id,
              })
            )
          }
          onCancel={() => navigate("/admin/tenants/list")}
        />
      </div>
    </Layout>
  );
};

// ---- Edit mode: unchanged from before — single-page Form.tsx, org fields only. ----
const TenantEditForm: React.FC<{
  tenantDetails: { data: any };
  loading: boolean;
  onSubmit: (data: Record<string, any>) => void;
  onCancel: () => void;
}> = ({ tenantDetails, loading, onSubmit, onCancel }) => {
  const fields: FormField[] = [
    {
      label: "Tenant Name",
      name: "tenantName",
      type: "text",
      placeholder: "Enter tenant name",
      validations: { required: true },
    },
    {
      label: "Tenant Email",
      name: "tenantEmail",
      type: "text",
      placeholder: "Enter tenant email address",
      validations: {
        required: true,
        regex: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
        message: "Invalid tenant email format",
      },
    },
    {
      label: "Tenant Mobile",
      name: "tenantMobile",
      type: "text",
      placeholder: "Enter tenant mobile number",
      validations: { required: true },
    },
    {
      label: "Address Line 1",
      name: "tenantAddressLine1",
      type: "text",
      placeholder: "Building, street",
      validations: { required: true },
    },
    {
      label: "Address Line 2",
      name: "tenantAddressLine2",
      type: "text",
      placeholder: "Area, landmark (optional)",
    },
    {
      label: "City",
      name: "tenantCity",
      type: "text",
    },
    {
      label: "State",
      name: "tenantState",
      type: "text",
    },
    {
      label: "Pincode",
      name: "tenantPincode",
      type: "text",
    },
    {
      label: "Tenant Company Size",
      name: "tenantCompanySize",
      type: "text",
      placeholder: "5-10, 10-50, 50-100 etc.",
      validations: { required: true },
    },
  ];

  const initialState = useMemo(
    () => ({ ...tenantDetails.data }),
    [tenantDetails.data]
  );

  return (
    <Card>
      <Form
        initialState={initialState as any}
        fields={fields}
        onSubmit={onSubmit}
        onCancel={onCancel}
        loading={loading}
      />
    </Card>
  );
};

// ---- Create mode: 2-step wizard, in sync with the self-serve sign-up flow. ----
const TENANT_STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Suspended", value: "suspended" },
];

export const TenantCreateWizard: React.FC<{
  loading: boolean;
  onSubmit: (data: Record<string, any>) => void;
}> = ({ loading, onSubmit }) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [checkingEmail, setCheckingEmail] = useState(false);

  const [ownerName, setOwnerName] = useState("");
  const [ownerNameError, setOwnerNameError] = useState("");
  const [tenantEmail, setTenantEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [tenantMobile, setTenantMobile] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [tenantName, setTenantName] = useState("");
  const [tenantNameError, setTenantNameError] = useState("");
  const [tenantIdentifier, setTenantIdentifier] = useState("");
  const [identifierRequiredError, setIdentifierRequiredError] = useState("");
  const identifierAvailability = useIdentifierAvailability(tenantIdentifier);
  const [locationLabel, setLocationLabel] = useState(
    DEFAULT_LOCATION_LABEL_PRESET.label
  );
  const [tenantGST, setTenantGST] = useState("");
  const [tenantAddressLine1, setTenantAddressLine1] = useState("");
  const [tenantAddressLine2, setTenantAddressLine2] = useState("");
  const [tenantCity, setTenantCity] = useState("");
  const [tenantState, setTenantState] = useState("");
  const [tenantPincode, setTenantPincode] = useState("");
  const [tenantCompanySize, setTenantCompanySize] = useState("");
  const [tenantStatus, setTenantStatus] = useState("active");

  // The slide track keeps both step panels mounted side by side (for the
  // horizontal transition), which makes the row stretch to the taller
  // panel's height even while showing the shorter one — most visible once
  // this wizard is dropped into a modal instead of a full page. Track the
  // active panel's real height and size the wrapper to that instead.
  const step1Ref = useRef<HTMLDivElement>(null);
  const step2Ref = useRef<HTMLDivElement>(null);
  const [trackHeight, setTrackHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const activeEl = step === 1 ? step1Ref.current : step2Ref.current;
    if (!activeEl) return;
    const update = () => setTrackHeight(activeEl.scrollHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(activeEl);
    return () => ro.disconnect();
  }, [step]);

  const handleNext = async (e: React.FormEvent) => {
    e.preventDefault();

    const nameErr = validateRequiredName(ownerName, "Owner name");
    const emailErr = validateEmail(tenantEmail);
    const mobileErr = validateMobile(tenantMobile);
    const passwordErr = validatePassword(password);
    setOwnerNameError(nameErr || "");
    setEmailError(emailErr || "");
    setMobileError(mobileErr || "");
    setPasswordError(passwordErr || "");
    if (nameErr || emailErr || mobileErr || passwordErr) return;

    setCheckingEmail(true);
    try {
      const res: any = await authTenantApis.checkEmail(tenantEmail);
      if (!res?.available) {
        setEmailError("An account with this email already exists.");
        setCheckingEmail(false);
        return;
      }
    } catch {
      // Fail open — the final submit still re-validates server-side.
    }
    setCheckingEmail(false);
    setStep(2);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const orgNameErr = validateRequiredName(tenantName, "Organization name");
    setTenantNameError(orgNameErr || "");

    if (!tenantIdentifier.trim()) {
      setIdentifierRequiredError("Organization identifier is required");
    } else {
      setIdentifierRequiredError("");
    }

    if (orgNameErr || !tenantIdentifier.trim()) return;
    if (identifierAvailability.status !== "available") return;

    const preset =
      LOCATION_LABEL_PRESETS.find((p) => p.label === locationLabel) ||
      DEFAULT_LOCATION_LABEL_PRESET;
    onSubmit({
      ownerName,
      tenantEmail,
      tenantMobile,
      password,
      tenantName,
      tenantIdentifier: tenantIdentifier.trim().toLowerCase().replace(/\s+/g, "-"),
      warehouseLabelSingular: preset.singular,
      warehouseLabelPlural: preset.plural,
      tenantGST: tenantGST || undefined,
      tenantAddressLine1: tenantAddressLine1 || undefined,
      tenantAddressLine2: tenantAddressLine2 || undefined,
      tenantCity: tenantCity || undefined,
      tenantState: tenantState || undefined,
      tenantPincode: tenantPincode || undefined,
      tenantCompanySize: tenantCompanySize || undefined,
      tenantStatus,
    });
  };

  return (
    <Card>
      <div className="mx-auto max-w-xl">
        <div className="mb-5 flex items-center gap-2">
          <StepDot active={step === 1} done={step > 1} num={1} />
          <div
            className={`h-[2px] flex-1 rounded-full transition-colors ${
              step > 1 ? "bg-primary-500" : "bg-gray-200 dark:bg-gray-700"
            }`}
          />
          <StepDot active={step === 2} done={false} num={2} />
        </div>

        <h3 className="mb-1 text-lg font-bold text-gray-900 dark:text-gray-100">
          {step === 1 ? "Owner account" : "Organization details"}
        </h3>
        <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
          {step === 1
            ? "Set up the tenant owner's login — they'll be able to sign in immediately."
            : "This tenant goes live immediately once created — no approval step, since you're creating it directly."}
        </p>

        <div
          className="overflow-hidden transition-[height] duration-300 ease-out"
          style={{ height: trackHeight }}
        >
          <div
            className="flex items-start transition-transform duration-300 ease-out"
            style={{ width: "200%", transform: step === 1 ? "translateX(0%)" : "translateX(-50%)" }}
          >
            {/* Step 1 */}
            <div ref={step1Ref} className="w-1/2 pr-4">
              <form className="space-y-3.5" onSubmit={handleNext}>
                <Input
                  label="Owner name"
                  required
                  placeholder="Jane Doe"
                  value={ownerName}
                  onChange={(e) => {
                    const v = e.target.value;
                    setOwnerName(v);
                    setOwnerNameError(validateRequiredName(v, "Owner name") || "");
                  }}
                  startAdornment={<User size={16} />}
                  error={!!ownerNameError}
                  helperText={ownerNameError || undefined}
                />
                <Input
                  label="Company email"
                  required
                  type="email"
                  placeholder="owner@company.com"
                  value={tenantEmail}
                  onChange={(e) => {
                    const v = e.target.value;
                    setTenantEmail(v);
                    setEmailError(validateEmail(v) || "");
                  }}
                  startAdornment={<Mail size={16} />}
                  error={!!emailError}
                  helperText={emailError || undefined}
                />
                <Input
                  label="Mobile number"
                  required
                  placeholder="98765 43210"
                  value={tenantMobile}
                  onChange={(e) => {
                    const v = e.target.value;
                    setTenantMobile(v);
                    setMobileError(validateMobile(v) || "");
                  }}
                  startAdornment={<span className="text-sm">+91</span>}
                  error={!!mobileError}
                  helperText={mobileError || undefined}
                />
                <PasswordField
                  value={password}
                  onChange={(v) => {
                    setPassword(v);
                    setPasswordError(validatePassword(v) || "");
                  }}
                  error={passwordError}
                  defaultHelperText="The owner signs in with this immediately — share it with them."
                />
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="submit" isLoading={checkingEmail}>
                    Next
                    <ArrowRight size={15} className="ml-1.5" />
                  </Button>
                </div>
              </form>
            </div>

            {/* Step 2 */}
            <div ref={step2Ref} className="w-1/2 pl-4">
              <form className="space-y-3.5" onSubmit={handleSubmit}>
                <Input
                  label="Organization name"
                  required
                  placeholder="Your Company Name"
                  value={tenantName}
                  onChange={(e) => {
                    const v = e.target.value;
                    setTenantName(v);
                    setTenantNameError(validateRequiredName(v, "Organization name") || "");
                  }}
                  startAdornment={<Building2 size={16} />}
                  error={!!tenantNameError}
                  helperText={tenantNameError || undefined}
                />
                <IdentifierField
                  value={tenantIdentifier}
                  onChange={(v) => {
                    setTenantIdentifier(v);
                    setIdentifierRequiredError("");
                  }}
                  status={identifierAvailability.status}
                  suggestions={identifierAvailability.suggestions}
                  error={identifierRequiredError || identifierAvailability.error}
                />

                <div className="flex flex-col items-start space-y-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    How do you name your location?
                  </label>
                  <div className="flex w-full items-center gap-2 rounded-md border border-gray-300 bg-white px-2 dark:border-gray-700 dark:bg-gray-800">
                    <span className="text-gray-500 dark:text-gray-400">
                      <Warehouse size={16} />
                    </span>
                    <select
                      className="h-9 w-full flex-1 bg-transparent text-sm text-gray-900 outline-none dark:text-gray-100"
                      value={locationLabel}
                      onChange={(e) => setLocationLabel(e.target.value)}
                    >
                      {LOCATION_LABEL_PRESETS.map((p) => (
                        <option key={p.label} value={p.label}>
                          {p.label} / {p.plural}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="GST"
                    optional
                    placeholder="GSTIN"
                    value={tenantGST}
                    onChange={(e) => setTenantGST(e.target.value)}
                  />
                  <Input
                    label="Company size"
                    optional
                    placeholder="5-10, 10-50..."
                    value={tenantCompanySize}
                    onChange={(e) => setTenantCompanySize(e.target.value)}
                  />
                </div>
                <Input
                  label="Address line 1"
                  optional
                  placeholder="Building, street"
                  value={tenantAddressLine1}
                  onChange={(e) => setTenantAddressLine1(e.target.value)}
                />
                <Input
                  label="Address line 2"
                  optional
                  placeholder="Area, landmark"
                  value={tenantAddressLine2}
                  onChange={(e) => setTenantAddressLine2(e.target.value)}
                />
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    label="City"
                    optional
                    value={tenantCity}
                    onChange={(e) => setTenantCity(e.target.value)}
                  />
                  <Input
                    label="State"
                    optional
                    value={tenantState}
                    onChange={(e) => setTenantState(e.target.value)}
                  />
                  <Input
                    label="Pincode"
                    optional
                    value={tenantPincode}
                    onChange={(e) => setTenantPincode(e.target.value)}
                  />
                </div>

                <Dropdown
                  label="Status"
                  value={tenantStatus}
                  options={TENANT_STATUS_OPTIONS}
                  onChange={setTenantStatus}
                />

                <div className="flex justify-between gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex items-center gap-1 px-3 text-sm font-semibold text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    <ArrowLeft size={15} />
                    Back
                  </button>
                  <Button
                    type="submit"
                    isLoading={loading}
                    disabled={
                      !!tenantIdentifier &&
                      identifierAvailability.status !== "available"
                    }
                  >
                    Create tenant
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

const StepDot: React.FC<{ active: boolean; done: boolean; num: number }> = ({ active, done, num }) => (
  <div
    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-extrabold transition-colors ${
      active || done
        ? "bg-primary-600 text-white"
        : "bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500"
    }`}
  >
    {done ? <Check size={13} /> : num}
  </div>
);

export default TenantForm;
